declare global {
    interface Window {
        HyperDX?: {
            init: (config: Record<string, unknown>) => void;
            setGlobalAttributes: (attrs: Record<string, string>) => void;
            addAction: (name: string, attrs?: Record<string, string>) => void;
        };
    }
}

interface ClickStackConfig {
    ingestUrl: string;
    apiKey: string;
    disableReplay: boolean;
}

let initialized = false;
let initStarted = false;

function getConfig(): ClickStackConfig | null {
    const ingestUrl = process.env.REACT_APP_CLICKSTACK_INGEST_URL;
    const apiKey = process.env.REACT_APP_CLICKSTACK_API_KEY;

    if (!ingestUrl || !apiKey) {
        return null;
    }

    const disableReplay = process.env.REACT_APP_CLICKSTACK_DISABLE_REPLAY === 'true';

    return { ingestUrl, apiKey, disableReplay };
}

function loadScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.crossOrigin = '';
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('ClickStack script failed to load'));
        document.head.appendChild(script);
    });
}

/**
 * Initializes ClickStack (HyperDX) tracking in a completely non-blocking manner.
 * - Deferred via requestIdleCallback/setTimeout so it never blocks rendering
 * - Script loaded async; all errors silently caught
 * - The application workflow is never blocked regardless of ClickStack state
 */
export function initClickStack(): void {
    if (initialized || initStarted) {
        return;
    }
    initStarted = true;

    const schedule =
        typeof window.requestIdleCallback === 'function'
            ? window.requestIdleCallback
            : (cb: () => void) => setTimeout(cb, 0);

    schedule(() => {
        try {
            const config = getConfig();
            if (!config) {
                initStarted = false;
                return;
            }

            loadScript('https://unpkg.com/@hyperdx/browser@0.24.0/build/index.js')
                .then(() => {
                    try {
                        if (!window.HyperDX) {
                            initStarted = false;
                            return;
                        }

                        window.HyperDX.init({
                            apiKey: config.apiKey,
                            service: 'shootproof-payments-web',
                            tracePropagationTargets: [/api/i],
                            consoleCapture: true,
                            advancedNetworkCapture: false,
                            maskAllInputs: true,
                            maskAllText: false,
                            disableIntercom: true,
                            disableReplay: config.disableReplay,
                            url: config.ingestUrl,
                            otelResourceAttributes: {
                                'deployment.environment':
                                    process.env.REACT_APP_CLICKSTACK_ENVIRONMENT
                            }
                        });

                        initialized = true;
                    } catch {
                        initStarted = false;
                        // Silently ignore init errors — never block the app
                    }
                })
                .catch(() => {
                    initStarted = false;
                    // Script load failure — silently ignore, app continues unaffected
                });
        } catch {
            initStarted = false;
            // Silently ignore any scheduling error
        }
    });
}

// Exported for testing purposes only
export function _isInitialized(): boolean {
    return initialized;
}

export function _reset(): void {
    initialized = false;
    initStarted = false;
}
